import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import ShowcaseView from "@/components/ShowcaseView";
import { usePublicShowcase } from "@/hooks/useShowcase";
import useFrameHeight from "@/hooks/useFrameHeight";
import "./ShowcaseEmbed.scss";

const withShowcaseSource = (url, key) => {
  if (!url || !key) return url;
  try {
    const parsed = new URL(url);
    parsed.searchParams.set("source", "showcase");
    parsed.searchParams.set("showcase", key);
    return parsed.toString();
  } catch {
    return url;
  }
};

export const buyerFormUrlOf = (data, key) => {
  const slug = data?.buyerFormSlug;
  const url =
    data?.buyerFormUrl ||
    (slug ? `${process.env.REACT_APP_BUYERLISTING_SERVICE_BASE_URL}/form/${encodeURIComponent(slug)}` : "");
  return withShowcaseSource(url, key || data?.key || data?.publicKey);
};

export const useShowcaseFeed = ({ showcaseKey, slug, host }) => {
  const [sort, setSort] = useState("featured");
  const [openId, setOpenId] = useState(null);
  const query = usePublicShowcase({ showcaseKey, slug, sort, host });
  const pages = query.data?.pages || [];
  const first = pages[0] || null;
  const items = useMemo(() => {
    const seen = new Set();
    return pages
      .flatMap((page) => page?.items || [])
      .filter((item) => (seen.has(item.wantAdId) ? false : seen.add(item.wantAdId)));
  }, [pages]);
  return { query, first, items, sort, setSort, openId, setOpenId };
};

export const showcaseViewProps = ({ query, first, items, sort, setSort, openId, setOpenId }, key) => ({
  brokerage: first?.brokerage,
  headline: first?.headline,
  items,
  sort,
  onSort: (next) => {
    setOpenId(null);
    setSort(next);
  },
  loading: query.isLoading,
  hasMore: Boolean(query.hasNextPage),
  loadingMore: query.isFetchingNextPage,
  onMore: () => query.fetchNextPage(),
  hostedUrl: first?.hostedUrl,
  buyerFormUrl: buyerFormUrlOf(first, key),
  showcaseKey: key || first?.key || first?.publicKey,
  openId,
  onOpenChange: setOpenId,
});

const ShowcaseEmbed = () => {
  const { key } = useParams();
  const [params] = useSearchParams();
  const host = params.get("host") || "";
  const feed = useShowcaseFeed({ showcaseKey: key, host });
  const rootRef = useRef(null);
  const hidden = feed.query.isError || (feed.first && feed.first.live === false);

  useEffect(() => {
    document.documentElement.classList.add("showcase-embed-document");
    return () => document.documentElement.classList.remove("showcase-embed-document");
  }, []);

  useFrameHeight(rootRef, !hidden);

  if (hidden) return null;

  return (
    <div className="showcase-embed" ref={rootRef}>
      <ShowcaseView {...showcaseViewProps(feed, key)} />
    </div>
  );
};

export default ShowcaseEmbed;
